// Hotbar selection + wieldable catalog. The bar's STORAGE lives in PlayerInventory (10 real slots --
// an item is on the bar or in the grid, never both); this class owns which slot is ACTIVE (keys 1..0)
// and the catalog metadata (display name + viewmodel id) for everything that can be wielded.
//
// Item ids are the canonical strings used everywhere: 'hammer', 'blueprint', 'rock', 'rifle'.

import { ITEMS, PlayerInventory, BAR_SIZE, type ItemId, type FoodId, type Stack } from './inventory';
import { CONSUMABLES } from './consumables';
import { CRAFTABLES, type CraftableId } from './craftables';
import { WEAPONS, WEAPON_IDS, type WeaponId } from './weapons';

// Tools + deployables have a fixed catalog; consumables (food/bandage) are catalog entries too so they can
// sit in a hotbar slot and be eaten with left-click. Campfire is a deployable like the chests.
export type HotbarItemId = 'hammer' | 'blueprint' | 'rock' | 'torch' | 'guidebook' | 'ak' | 'chest' | 'bigchest' | 'campfire'
  | 'wood_axe' | 'wood_pickaxe' | 'stone_axe' | 'stone_pickaxe' | 'metal_axe' | 'metal_pickaxe' | WeaponId | CraftableId | FoodId;

// One catalog entry per wieldable item: display name + the id the viewmodel system swaps to when the
// item becomes active. (Which slot an item sits in is inventory state now, not catalog data.)
export interface HotbarItem {
  id: HotbarItemId;
  name: string;
  viewmodelId: string;
}

const TOOL_ITEMS: Record<'hammer' | 'blueprint' | 'rock' | 'torch' | 'guidebook' | 'ak' | 'chest' | 'bigchest' | 'campfire'
  | 'wood_axe' | 'wood_pickaxe' | 'stone_axe' | 'stone_pickaxe' | 'metal_axe' | 'metal_pickaxe', HotbarItem> = {
  hammer: { id: 'hammer', name: 'Hammer', viewmodelId: 'hammer' },
  blueprint: { id: 'blueprint', name: 'Blueprint', viewmodelId: 'blueprint' },
  rock: { id: 'rock', name: 'Rock', viewmodelId: 'rock' },
  torch: { id: 'torch', name: 'Torch', viewmodelId: 'torch' },
  guidebook: { id: 'guidebook', name: 'Guide', viewmodelId: 'guidebook' },
  ak: { id: 'ak', name: 'AK', viewmodelId: 'ak' }, // legacy id; kept for the AK code path, never granted
  wood_axe: { id: 'wood_axe', name: 'Wooden Axe', viewmodelId: 'wood_axe' },
  wood_pickaxe: { id: 'wood_pickaxe', name: 'Wooden Pickaxe', viewmodelId: 'wood_pickaxe' },
  stone_axe: { id: 'stone_axe', name: 'Stone Axe', viewmodelId: 'stone_axe' },
  stone_pickaxe: { id: 'stone_pickaxe', name: 'Stone Pickaxe', viewmodelId: 'stone_pickaxe' },
  metal_axe: { id: 'metal_axe', name: 'Metal Axe', viewmodelId: 'metal_axe' },
  metal_pickaxe: { id: 'metal_pickaxe', name: 'Metal Pickaxe', viewmodelId: 'metal_pickaxe' },
  chest: { id: 'chest', name: 'Small Chest', viewmodelId: 'chest' },
  bigchest: { id: 'bigchest', name: 'Large Chest', viewmodelId: 'bigchest' },
  campfire: { id: 'campfire', name: 'Campfire', viewmodelId: 'campfire' },
};
// Consumables become catalog entries automatically from the CONSUMABLES table (no held model).
const CONSUMABLE_ITEMS = Object.fromEntries(
  (Object.keys(CONSUMABLES) as ItemId[]).map((id) => [id, { id: id as HotbarItemId, name: ITEMS[id].name, viewmodelId: 'food' }]),
) as Record<FoodId, HotbarItem>;
// Every craftable deployable gets a catalog entry so it can sit in a hotbar slot. They wield
// empty-handed (HELD_MESH lacks them) and place via the CRAFTABLE_IDS ghost branch.
const CRAFTABLE_HOTBAR_ITEMS = Object.fromEntries(
  CRAFTABLES.map((c) => [c.id, { id: c.id, name: c.label, viewmodelId: c.id }]),
) as Record<CraftableId, HotbarItem>;
// Weapons: wieldable; viewmodelId = the weapon id (held model).
const WEAPON_HOTBAR_ITEMS = Object.fromEntries(
  WEAPON_IDS.map((id) => [id, { id, name: WEAPONS[id].name, viewmodelId: id }]),
) as Record<WeaponId, HotbarItem>;
export const HOTBAR_ITEMS: Record<HotbarItemId, HotbarItem> = { ...TOOL_ITEMS, ...CRAFTABLE_HOTBAR_ITEMS, ...WEAPON_HOTBAR_ITEMS, ...CONSUMABLE_ITEMS };

export const HOTBAR_SLOTS = BAR_SIZE; // slots 1..0 on the keyboard
const DEFAULT_ACTIVE_SLOT = 1; // ROCK (slot 1) selected on spawn -- owner default

export class Hotbar {
  private active = DEFAULT_ACTIVE_SLOT; // 1-based active slot
  private lastActive: HotbarItemId | null = null; // last item we emitted for (detects the active stack changing under us)

  // Fired whenever the active slot OR the item in it changes (so the UI repaints and systems re-wield).
  onChange: ((item: HotbarItemId | null, slot: number) => void) | null = null;

  constructor(private readonly inv: PlayerInventory) {
    // The bar's contents can change without a select (stack emptied, drag/drop move, pickup routing).
    // Re-emit only when the ACTIVE slot's item actually changed, so a wood gather doesn't re-wield.
    this.inv.subscribe(() => {
      const it = this.activeItem();
      if (it !== this.lastActive) this.layoutChanged();
    });
  }

  // Select a 1-based slot (1..10). Out-of-range is ignored; re-selecting the active slot is a no-op.
  select(slot: number): void {
    if (slot < 1 || slot > HOTBAR_SLOTS) return;
    if (slot === this.active) return;
    this.active = slot;
    this.layoutChanged();
  }

  // Step the active slot by dir (+1 next / -1 prev), wrapping around the bar. Drives the first-person
  // scroll-wheel hotbar switch.
  cycle(dir: number): void {
    const step = dir > 0 ? 1 : -1;
    const next = ((this.active - 1 + step) % HOTBAR_SLOTS + HOTBAR_SLOTS) % HOTBAR_SLOTS + 1;
    this.select(next);
  }

  // The active slot, 1-based (10 means the '0' key slot).
  activeSlot(): number {
    return this.active;
  }

  // The item id in the active slot, or null when the slot is empty.
  activeItem(): HotbarItemId | null {
    return this.itemAt(this.active);
  }

  // The catalog entry for the active item, or null when the slot is empty.
  activeEntry(): HotbarItem | null {
    const id = this.activeItem();
    return id ? HOTBAR_ITEMS[id] : null;
  }

  // The item id in a given 1-based slot, or null. Used by the UI to render the bar.
  itemAt(slot: number): HotbarItemId | null {
    if (slot < 1 || slot > HOTBAR_SLOTS) return null;
    return (this.inv.barStack(slot - 1)?.id as HotbarItemId | undefined) ?? null;
  }

  // The whole stack in a 1-based slot (the UI shows its count).
  stackAt(slot: number): Stack | null {
    if (slot < 1 || slot > HOTBAR_SLOTS) return null;
    return this.inv.barStack(slot - 1);
  }

  // The 1-based slot currently holding `id`, or 0 if none.
  slotOf(id: HotbarItemId): number {
    for (let s = 1; s <= HOTBAR_SLOTS; s++) if (this.itemAt(s) === id) return s;
    return 0;
  }

  // First empty 1-based slot, or 0 when the bar is full (double-click equip finds a home with this).
  firstFreeSlot(): number {
    for (let s = 1; s <= HOTBAR_SLOTS; s++) if (!this.itemAt(s)) return s;
    return 0;
  }

  private layoutChanged(): void {
    this.lastActive = this.activeItem();
    this.onChange?.(this.lastActive, this.active);
  }
}
